/**
 * Full Box Calculator Module
 * Handles pricing calculations for full box variants
 */
class FullBoxCalculator {
  constructor() {
    this.elements = this.initializeElements();
    this.config = this.getConfiguration();
    
    // Store original variant ID and get Landers variant ID
    this.originalVariantId = this.getOriginalVariantId();
    this.landersVariantId = this.getLandersVariantId();
    
    if (this.elements.calculator && this.elements.input) {
      this.bindEvents();
      this.initialize();
    }
  }

  /**
   * Initialize DOM elements
   */
  initializeElements() {
    return {
      calculator: document.querySelector('.fullbox-calculator'),
      input: document.getElementById('fullbox-sqft-input'),
      quantityDisplay: document.getElementById('fullbox-quantity-number'),
      boxUnit: document.getElementById('fullbox-box-unit'),
      totalPrice: document.getElementById('fullbox-total-price'),
      wasteCheckbox: document.getElementById('fullbox-waste-checkbox'),
      variantAttributes: document.querySelector('.variant_attributes'),
      addToCartBtn: document.getElementById('fullbox-add-to-cart')
    };
  }

  /**
   * Get original variant ID from the cart form
   */
  getOriginalVariantId() {
    const variantIdInput = document.querySelector('form[action="/cart/add"] input[name="id"]');
    return variantIdInput ? variantIdInput.value : null;
  }

  /**
   * Get Landers variant ID from variant options
   */
  getLandersVariantId() {
    const landersVariant = document.querySelector('input[name="variant-radio"][data-title="Landers"]');
    return landersVariant ? landersVariant.value : null;
  }

  /**
   * Get configuration from variant attributes
   */
  getConfiguration() {
    const { variantAttributes } = this.elements;
    
    if (!variantAttributes) {
      return {
        sqftPerBox: 1,
        originalPrice: 0,
        discountedPrice: 0,
        discountThreshold: 1000
      };
    }

         return {
       sqftPerBox: parseFloat(variantAttributes.getAttribute('data-sqft')) || 1,
       originalPrice: parseFloat(variantAttributes.getAttribute('data-original-price')) || 0,
       discountedPrice: parseFloat(variantAttributes.getAttribute('data-discounted-price')) || 0,
       fullBoxPrice: parseFloat(variantAttributes.getAttribute('data-fullbox-price')) || 0,
       discountThreshold: 1000 // 1000 sqft threshold for discount pricing
     };
  }

  /**
   * Initialize calculator
   */
  initialize() {
    // Reset calculator to initial state (but preserve any existing checkbox state)
    const { input } = this.elements;
    if (input) input.value = '';
    
    // Set default state with proper waste calculation
    this.setDefaultState();
    
    // Add keyboard support for better accessibility
    this.addKeyboardSupport();
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    const { input, wasteCheckbox, addToCartBtn } = this.elements;

    if (input) {
      // Real-time calculation as user types
      input.addEventListener('input', () => this.calculate());
      input.addEventListener('change', () => this.calculate());
      
      // Prevent negative values
      input.addEventListener('keydown', this.preventInvalidInput.bind(this));
      input.addEventListener('paste', this.handlePaste.bind(this));
    }

    if (wasteCheckbox) {
      wasteCheckbox.addEventListener('change', () => this.calculate());
    }

    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', () => this.handleAddToCart());
    }
  }

  /**
   * Add keyboard support for accessibility
   */
  addKeyboardSupport() {
    const { wasteCheckbox } = this.elements;
    
    if (wasteCheckbox) {
      // Allow space bar to toggle checkbox
      wasteCheckbox.addEventListener('keydown', (event) => {
        if (event.key === ' ') {
          event.preventDefault();
          wasteCheckbox.checked = !wasteCheckbox.checked;
          this.calculate();
        }
      });
    }
  }

  /**
   * Prevent invalid input (negative numbers, letters, etc.)
   */
  preventInvalidInput(event) {
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (event.keyCode === 65 && event.ctrlKey === true) ||
        (event.keyCode === 67 && event.ctrlKey === true) ||
        (event.keyCode === 86 && event.ctrlKey === true) ||
        (event.keyCode === 88 && event.ctrlKey === true) ||
        // Allow: home, end, left, right, down, up
        (event.keyCode >= 35 && event.keyCode <= 40)) {
      return;
    }
    
    // Prevent: everything else that isn't a number or decimal point
    if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && 
        (event.keyCode < 96 || event.keyCode > 105) && 
        event.keyCode !== 190 && event.keyCode !== 110) {
      event.preventDefault();
    }
  }

  /**
   * Handle paste events to validate input
   */
  handlePaste(event) {
    event.preventDefault();
    const pasteData = (event.clipboardData || window.clipboardData).getData('text');
    const numericValue = parseFloat(pasteData);
    
    // Only allow positive numbers
    if (!isNaN(numericValue) && numericValue >= 0) {
      event.target.value = numericValue;
      this.calculate();
    }
  }

  /**
   * Update variant ID based on discount threshold
   */
  updateVariantId(showDiscount) {
    const variantIdInput = document.querySelector('form[action="/cart/add"] input[name="id"]');
    
    if (!variantIdInput) {
      return;
    }
    
    // Only perform variant switching if both variant IDs are available
    if (!this.isLandersVariantAvailable()) {
      return;
    }
    
    if (showDiscount) {
      // Switch to Landers variant for discount pricing
      if (variantIdInput.value !== this.landersVariantId) {
        variantIdInput.value = this.landersVariantId;
      }
    } else {
      // Switch back to original variant for regular pricing
      if (variantIdInput.value !== this.originalVariantId) {
        variantIdInput.value = this.originalVariantId;
      }
    }
  }

  /**
   * Main calculation function
   */
     calculate() {
     const { input, quantityDisplay, totalPrice, wasteCheckbox } = this.elements;
     const { sqftPerBox, fullBoxPrice, originalPrice, discountedPrice, discountThreshold } = this.config;
     
     if (!input || !quantityDisplay || !totalPrice) return;

     // Get input value
     const inputValue = parseFloat(input.value) || 0;
     
     // Handle zero or empty input - only reset if user has actually interacted
     if (inputValue <= 0) {
       // If input is truly empty (not just 0), maintain default state
       if (input.value === '' || input.value === null || input.value === undefined) {
         this.setDefaultState();
         return;
       } else {
         // User entered 0, so reset to 0 state
         this.displayResults(0, 0, 0, false);
         this.updateShopifyQuantity(0);
         this.updateAddToCartButton(false);
         this.updateVariantId(false); // Use original variant for 0 state
         return;
       }
     }

     // Calculate base number of boxes needed from user input (round up to nearest box)
     let boxesNeeded = Math.ceil(inputValue / sqftPerBox);
     
     // Calculate actual total square footage based on box quantity
     let totalSqft = boxesNeeded * sqftPerBox;
     
     // Apply waste if checkbox is checked (add 10% to total sqft and recalculate boxes)
     if (wasteCheckbox && wasteCheckbox.checked) {
       totalSqft = totalSqft * 1.1; // Add 10% to total sqft
       boxesNeeded = Math.ceil(totalSqft / sqftPerBox); // Recalculate boxes from new total
     }

     // Calculate original price: data-sqft * data-original-price * boxes quantity
     const originalPriceValue = sqftPerBox * originalPrice * boxesNeeded;
     
     // Calculate discounted price: data-sqft * data-discounted-price * boxes quantity
     let discountedPriceValue = 0;
     let showDiscount = false;
     if (totalSqft >= discountThreshold) {
       discountedPriceValue = sqftPerBox * discountedPrice * boxesNeeded;
       showDiscount = true;
     }

     // Update display and form
     this.displayResults(boxesNeeded, originalPriceValue, discountedPriceValue, showDiscount);
     this.updateShopifyQuantity(boxesNeeded);
     this.updateAddToCartButton(true);
     this.updateVariantId(showDiscount); // Switch variant ID based on discount threshold
     
     // Trigger custom event for external integrations
     this.triggerCalculationEvent(inputValue, boxesNeeded, showDiscount ? discountedPriceValue : originalPriceValue);
   }

  /**
   * Display calculation results
   */
  displayResults(boxes, originalPrice, discountedPrice, showDiscount) {
    const { quantityDisplay, boxUnit, totalPrice: totalPriceElement } = this.elements;
    
    if (quantityDisplay) {
      quantityDisplay.textContent = boxes;
    }
    
    if (boxUnit) {
      boxUnit.textContent = boxes === 1 ? 'box' : 'boxes';
    }
    
    if (totalPriceElement) {
      if (showDiscount) {
        // Show discounted price (left, orange) and original price (right, grey strikethrough)
        totalPriceElement.innerHTML = `
          <span class="discounted-price">$${this.formatPriceWithCommas(discountedPrice)}</span>
          <span class="original-price">$${this.formatPriceWithCommas(originalPrice)}</span>
        `;
        totalPriceElement.classList.add('discounted');
      } else {
        // Show regular original price
        totalPriceElement.textContent = `$${this.formatPriceWithCommas(originalPrice)}`;
        totalPriceElement.classList.remove('discounted');
      }
    }
  }

  /**
   * Update Shopify form quantity inputs
   */
  updateShopifyQuantity(quantity) {
    // Update main Shopify quantity input
    const shopifyQuantityInput = document.querySelector('form[action="/cart/add"] input[name="quantity"]');
    if (shopifyQuantityInput) {
      shopifyQuantityInput.value = quantity;
    }

    // Update custom quantity selector if it exists
    const customQuantityInput = document.querySelector('.custom-quantity__input');
    if (customQuantityInput) {
      customQuantityInput.value = quantity;
    }

    // Update any other quantity inputs that might exist
    const allQuantityInputs = document.querySelectorAll('input[name="quantity"]');
    allQuantityInputs.forEach(input => {
      if (input !== shopifyQuantityInput && input !== customQuantityInput) {
        input.value = quantity;
      }
    });
  }

  /**
   * Reset calculator input (preserves waste checkbox state)
   */
  resetCalculator() {
    const { input } = this.elements;
    
    if (input) input.value = '';
    
    // Don't reset waste checkbox - users might want to keep it checked
    // Don't set display values here - setDefaultState() will handle them
  }

  /**
   * Set default state with 1 box and corresponding price (respecting waste checkbox)
   */
  setDefaultState() {
    const { quantityDisplay, boxUnit, totalPrice, wasteCheckbox } = this.elements;
    const { sqftPerBox, originalPrice, discountedPrice, discountThreshold } = this.config;
    
    // Start with 1 box worth of sqft
    let defaultBoxes = 1;
    let totalSqft = sqftPerBox * defaultBoxes;
    
    // Apply waste if checkbox is checked (add 10% to total sqft and recalculate boxes)
    if (wasteCheckbox && wasteCheckbox.checked) {
      totalSqft = totalSqft * 1.1; // Add 10% to total sqft
      defaultBoxes = Math.ceil(totalSqft / sqftPerBox); // Recalculate boxes from new total
    }
    
    if (quantityDisplay) {
      quantityDisplay.textContent = defaultBoxes;
    }
    
    if (boxUnit) {
      boxUnit.textContent = defaultBoxes === 1 ? 'box' : 'boxes';
    }
    
    // Calculate original price: data-sqft * data-original-price * boxes quantity
    const originalPriceValue = sqftPerBox * originalPrice * defaultBoxes;
    
    // Calculate discounted price: data-sqft * data-discounted-price * boxes quantity
    let discountedPriceValue = 0;
    let showDiscount = false;
    if (totalSqft >= discountThreshold) {
      discountedPriceValue = sqftPerBox * discountedPrice * defaultBoxes;
      showDiscount = true;
    }
    
    if (totalPrice) {
      if (showDiscount) {
        // Show discounted price (left, orange) and original price (right, grey strikethrough)
        totalPrice.innerHTML = `
          <span class="discounted-price">$${this.formatPriceWithCommas(discountedPriceValue)}</span>
          <span class="original-price">$${this.formatPriceWithCommas(originalPriceValue)}</span>
        `;
        totalPrice.classList.add('discounted');
      } else {
        // Show regular original price
        totalPrice.textContent = `$${this.formatPriceWithCommas(originalPriceValue)}`;
        totalPrice.classList.remove('discounted');
      }
    }
    
    // Update Shopify quantity and enable add to cart
    this.updateShopifyQuantity(defaultBoxes);
    this.updateAddToCartButton(true);
    this.updateVariantId(showDiscount); // Switch variant ID based on discount threshold
  }

  /**
   * Show calculator
   */
  show() {
    if (this.elements.calculator) {
      this.elements.calculator.classList.add('active');
      this.elements.calculator.classList.remove('fullbox-calculator--hidden');
      this.elements.calculator.style.display = 'block';
    }
  }

  /**
   * Hide calculator
   */
  hide() {
    if (this.elements.calculator) {
      this.elements.calculator.classList.remove('active');
      this.elements.calculator.classList.add('fullbox-calculator--hidden');
      this.elements.calculator.style.display = 'none';
    }
  }

  /**
   * Update configuration (useful when variant changes)
   */
  updateConfiguration() {
    this.config = this.getConfiguration();
    
    // Refresh variant IDs in case the selected variant changed
    this.originalVariantId = this.getOriginalVariantId();
    this.landersVariantId = this.getLandersVariantId();
    
    this.calculate(); // Recalculate with new configuration
  }

  /**
   * Check if Landers variant switching is available
   */
  isLandersVariantAvailable() {
    return this.landersVariantId && this.originalVariantId;
  }

  /**
   * Trigger custom event for external integrations
   */
  triggerCalculationEvent(inputValue, boxes, totalPrice) {
    const event = new CustomEvent('fullboxCalculated', {
      detail: {
        inputValue,
        boxes,
        totalPrice,
        timestamp: Date.now()
      }
    });
    
    document.dispatchEvent(event);
  }

  /**
   * Public API methods
   */
  getCalculationResults() {
    const { input, quantityDisplay, totalPrice } = this.elements;
    
    return {
      inputValue: parseFloat(input?.value) || 0,
      boxes: parseInt(quantityDisplay?.textContent) || 0,
      totalPrice: parseFloat(totalPrice?.textContent?.replace('$', '')) || 0
    };
  }

  /**
   * Set input value programmatically
   */
  setInputValue(value) {
    if (this.elements.input) {
      this.elements.input.value = value;
      this.calculate();
    }
  }

  /**
   * Set waste checkbox state
   */
  setWasteRecommendation(checked) {
    if (this.elements.wasteCheckbox) {
      this.elements.wasteCheckbox.checked = checked;
      this.calculate();
    }
  }

  /**
   * Format price with commas and proper decimal places
   */
  formatPriceWithCommas(price) {
    return price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  /**
   * Update add to cart button state
   */
  updateAddToCartButton(enabled) {
    const { addToCartBtn } = this.elements;
    
    if (addToCartBtn) {
      addToCartBtn.disabled = !enabled;
      if (!enabled) {
        addToCartBtn.textContent = 'Add to Cart';
      }
    }
  }

  /**
   * Set loading state
   */
  setLoadingState(loading) {
    const { addToCartBtn } = this.elements;
    
    if (addToCartBtn) {
      addToCartBtn.disabled = loading;
      
      if (loading) {
        addToCartBtn.innerHTML = `
          <span class="btn-spinner"></span>
          <span class="btn-text">ADDING TO CART</span>
        `;
        addToCartBtn.classList.add('loading');
      } else {
        addToCartBtn.innerHTML = 'ADD TO CART';
        addToCartBtn.classList.remove('loading');
      }
    }
  }

  /**
   * Set success state
   */
  setSuccessState() {
    const { addToCartBtn } = this.elements;
    
    if (addToCartBtn) {
      addToCartBtn.innerHTML = `
        <span class="btn-checkmark">✓</span>
        <span class="btn-text">ADDED TO CART</span>
      `;
      addToCartBtn.classList.add('success');
      
      // Reset after 2 seconds
      setTimeout(() => {
        addToCartBtn.innerHTML = 'ADD TO CART';
        addToCartBtn.classList.remove('success');
        addToCartBtn.disabled = false;
      }, 2000);
    }
  }

  /**
   * Show toast notification
   */
  showToast(message, type = 'info') {
    // Remove existing toast
    const existingToast = document.querySelector('.fullbox-toast');
    if (existingToast) {
      existingToast.remove();
    }

    // Create toast element
    const toast = document.createElement('div');
    toast.className = `fullbox-toast fullbox-toast--${type}`;
    toast.innerHTML = `
      <div class="fullbox-toast__content">
        <span class="fullbox-toast__icon">${type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'}</span>
        <span class="fullbox-toast__message">${message}</span>
      </div>
      <button class="fullbox-toast__close" aria-label="Close notification">×</button>
    `;

    // Add to page
    document.body.appendChild(toast);

    // Add event listener for close button
    const closeBtn = toast.querySelector('.fullbox-toast__close');
    closeBtn.addEventListener('click', () => {
      toast.classList.add('fullbox-toast--hiding');
      setTimeout(() => toast.remove(), 300);
    });

    // Auto remove after 5 seconds
    setTimeout(() => {
      if (toast.parentNode) {
        toast.classList.add('fullbox-toast--hiding');
        setTimeout(() => toast.remove(), 300);
      }
    }, 5000);

    // Trigger animation
    setTimeout(() => toast.classList.add('fullbox-toast--visible'), 100);
  }

  /**
   * Update cart count
   */
  updateCartCount() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartCountElements = document.querySelectorAll('[data-cart-count]');
        cartCountElements.forEach(element => {
          element.textContent = cart.item_count;
        });
      })
      .catch(error => {
        console.warn('Could not update cart count:', error);
      });
  }

  /**
   * Handle add to cart button click
   */
  async handleAddToCart() {
    const { addToCartBtn } = this.elements;
    const { input, quantityDisplay } = this.elements;
    
    if (!addToCartBtn || !quantityDisplay) return;

    const quantity = parseInt(quantityDisplay.textContent) || 0;

    // Only check if we have a valid quantity to add (input value is not required for default state)
    if (quantity <= 0) {
      this.showToast('Please enter a valid square footage amount', 'error');
      return;
    }

    // Get current variant ID from the main product form
    const variantIdInput = document.querySelector('form[action="/cart/add"] input[name="id"]');
    if (!variantIdInput || !variantIdInput.value) {
      this.showToast('Please select a product variant', 'error');
      return;
    }

    const variantId = variantIdInput.value;

    this.setLoadingState(true);

    try {
      // Add to cart via Shopify Cart API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: quantity
          }]
        })
      });

      if (response.ok) {
        this.setLoadingState(false);
        this.setSuccessState();
        this.showToast(`${quantity} box${quantity > 1 ? 'es' : ''} added to cart successfully!`, 'success');
        
        // Update cart count if available
        this.updateCartCount();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add to cart');
      }
    } catch (error) {
      this.showToast('Error adding to cart: ' + error.message, 'error');
      this.setLoadingState(false);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Initialize full box calculator
  window.fullBoxCalculator = new FullBoxCalculator();
  
  // Make it globally accessible for debugging and external integrations
  window.FullBoxCalculator = FullBoxCalculator;
});

// Export for module systems (if needed)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FullBoxCalculator;
} 